import { useState, useEffect } from 'react'
import { Edit, Search, ToggleLeft, ToggleRight, Save, X } from 'lucide-react'
import Card from '@components/ui/Card'
import Button from '@components/ui/Button'
import Input from '@components/ui/Input'
import { useToast } from '@hooks/useToast'
import { getEventos, updateEvento, toggleEventoStatus } from '@services/eventService'

export default function EventManagement() {
  const toast = useToast()
  const [eventos, setEventos] = useState([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [editingEvent, setEditingEvent] = useState(null)
  const [editForm, setEditForm] = useState({
    grupo_evento: '',
    dsc_even: ''
  })

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [totalCount, setTotalCount] = useState(0)
  const [pageSize] = useState(20)

  // Grupo options state
  const [grupoOptions, setGrupoOptions] = useState([])
  const [loadingGrupos, setLoadingGrupos] = useState(false)

  useEffect(() => {
    loadEventos(currentPage, searchTerm)
    loadGrupoOptions()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Debounced search effect
  useEffect(() => {
    const timer = setTimeout(() => {
      if (currentPage !== 1) {
        setCurrentPage(1) // Reset to first page when searching
      } else {
        loadEventos(1, searchTerm)
      }
    }, 500) // 500ms debounce

    return () => clearTimeout(timer)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm])

  const loadEventos = async (page = 1, search = '') => {
    try {
      setLoading(true)
      const params = { page, page_size: pageSize }
      if (search.trim()) {
        params.search = search.trim()
      }

      const response = await getEventos(params)

      // Handle different response formats
      if (response.results) {
        // Paginated response
        setEventos(response.results)
        setTotalCount(response.count || 0)
        setTotalPages(Math.ceil((response.count || 0) / pageSize))
      } else if (Array.isArray(response)) {
        // Non-paginated response (fallback)
        setEventos(response)
        setTotalCount(response.length)
        setTotalPages(Math.ceil(response.length / pageSize))
      } else {
        setEventos([])
        setTotalCount(0)
        setTotalPages(1)
      }
    } catch (error) {
      toast.error('Erro ao carregar eventos: ' + error.message)
      setEventos([])
      setTotalCount(0)
      setTotalPages(1)
    } finally {
      setLoading(false)
    }
  }

  const handleEdit = (evento) => {
    setEditingEvent(evento.id)
    setEditForm({
      grupo_evento: evento.grupo_evento || '',
      dsc_even: evento.dsc_even || ''
    })
  }

  const handleCancelEdit = () => {
    setEditingEvent(null)
    setEditForm({
      grupo_evento: '',
      dsc_even: ''
    })
  }

  const handleSaveEdit = async () => {
    try {
      await updateEvento(editingEvent, editForm)
      toast.success('Evento atualizado com sucesso!')
      setEditingEvent(null)
      loadEventos()
    } catch (error) {
      toast.error('Erro ao atualizar evento: ' + error.message)
    }
  }

  const handleToggleStatus = async (evento) => {
    try {
      const newStatus = !evento.is_active
      await toggleEventoStatus(evento.id, newStatus)
      toast.success(`Evento ${newStatus ? 'ativado' : 'desativado'} com sucesso!`)
      loadEventos()
    } catch (error) {
      toast.error('Erro ao alterar status do evento: ' + error.message)
    }
  }

  const loadGrupoOptions = async () => {
    try {
      setLoadingGrupos(true)
      const response = await getEventos({ page_size: 1000 }) // Carregar muitos para ter todas as opções
      
      // Extrair grupos únicos dos eventos
      const grupos = [...new Set(
        response.results
          ?.filter(evento => evento.grupo_evento && evento.grupo_evento.trim())
          .map(evento => evento.grupo_evento.trim())
          .sort()
      )]
      
      setGrupoOptions(grupos)
    } catch (error) {
      console.error('Erro ao carregar opções de grupo:', error)
      setGrupoOptions([])
    } finally {
      setLoadingGrupos(false)
    }
  }

  const handleSearchChange = (value) => {
    setSearchTerm(value)
  }

  const handlePageChange = (page) => {
    setCurrentPage(page)
    loadEventos(page, searchTerm)
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
        <span className="ml-2 text-gray-600 dark:text-gray-400">Carregando eventos...</span>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
            Manutenção de Eventos
          </h2>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Gerencie os eventos do sistema de folha de pagamento
          </p>
        </div>
      </div>

      {/* Barra de Pesquisa */}
      <Card>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
          <Input
            type="text"
            placeholder="Buscar por código, descrição ou grupo..."
            value={searchTerm}
            onChange={(e) => handleSearchChange(e.target.value)}
            className="pl-10"
          />
        </div>
      </Card>

      {/* Tabela de Eventos */}
      <Card>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-gray-800">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Código
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Descrição
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Grupo
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Tipo
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Ações
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
              {eventos.map((evento) => (
                <tr key={evento.id} className={!evento.is_active ? 'opacity-60' : ''}>
                  <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                    {evento.cod_even}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                    {editingEvent === evento.id ? (
                      <Input
                        value={editForm.dsc_even}
                        onChange={(e) => setEditForm(prev => ({ ...prev, dsc_even: e.target.value }))}
                        className="w-full"
                      />
                    ) : (
                      evento.dsc_even || '-'
                    )}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                    {editingEvent === evento.id ? (
                      <select
                        value={editForm.grupo_evento}
                        onChange={(e) => setEditForm(prev => ({ ...prev, grupo_evento: e.target.value }))}
                        className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                        disabled={loadingGrupos}
                      >
                        <option value="">Selecione um grupo...</option>
                        {grupoOptions.map((grupo) => (
                          <option key={grupo} value={grupo}>
                            {grupo}
                          </option>
                        ))}
                      </select>
                    ) : (
                      evento.grupo_evento || '-'
                    )}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    {evento.v_d_even === 'D' ? 'Débito' : evento.v_d_even === 'V' ? 'Crédito' : '-'}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    <button
                      onClick={() => handleToggleStatus(evento)}
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        evento.is_active
                          ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                          : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                      }`}
                    >
                      {evento.is_active ? (
                        <>
                          <ToggleRight className="w-3 h-3 mr-1" />
                          Ativo
                        </>
                      ) : (
                        <>
                          <ToggleLeft className="w-3 h-3 mr-1" />
                          Inativo
                        </>
                      )}
                    </button>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm font-medium">
                    {editingEvent === evento.id ? (
                      <div className="flex space-x-2">
                        <Button
                          size="sm"
                          onClick={handleSaveEdit}
                          className="bg-green-600 hover:bg-green-700"
                        >
                          <Save className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={handleCancelEdit}
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    ) : (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleEdit(evento)}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {eventos.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            {searchTerm ? 'Nenhum evento encontrado para a busca.' : 'Nenhum evento cadastrado.'}
          </div>
        )}
      </Card>

      {/* Pagination Controls */}
      {totalPages > 1 && (
        <Card>
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-700 dark:text-gray-300">
              Mostrando {((currentPage - 1) * pageSize) + 1} a {Math.min(currentPage * pageSize, totalCount)} de {totalCount} eventos
            </div>
            <div className="flex items-center space-x-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
              >
                Anterior
              </Button>

              {/* Page Numbers */}
              <div className="flex space-x-1">
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum
                  if (totalPages <= 5) {
                    pageNum = i + 1
                  } else if (currentPage <= 3) {
                    pageNum = i + 1
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i
                  } else {
                    pageNum = currentPage - 2 + i
                  }

                  return (
                    <Button
                      key={pageNum}
                      variant={currentPage === pageNum ? "primary" : "outline"}
                      size="sm"
                      onClick={() => handlePageChange(pageNum)}
                      className="w-8 h-8 p-0"
                    >
                      {pageNum}
                    </Button>
                  )
                })}
              </div>

              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
              >
                Próxima
              </Button>
            </div>
          </div>
        </Card>
      )}

      {/* Estatísticas */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <div className="text-center">
            <div className="text-2xl font-bold text-blue-600">{totalCount}</div>
            <div className="text-sm text-gray-600 dark:text-gray-400">Total de Eventos</div>
          </div>
        </Card>
        <Card>
          <div className="text-center">
            <div className="text-2xl font-bold text-green-600">
              {eventos.filter(e => e.is_active).length}
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400">Eventos Ativos (Página Atual)</div>
          </div>
        </Card>
        <Card>
          <div className="text-center">
            <div className="text-2xl font-bold text-red-600">
              {eventos.filter(e => !e.is_active).length}
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400">Eventos Inativos (Página Atual)</div>
          </div>
        </Card>
      </div>
    </div>
  )
}